import dayjs from "dayjs";
import { calculateLineAmounts } from "../../../../utils/invoiceTotals";
import { numberToLetters } from "../../../../utils/utils";

export type PeruDocumentKind = "boleta" | "factura" | "credit_memo";

export const PERU_DOCUMENT_LABELS: Record<PeruDocumentKind, string> = {
  boleta: "BOLETA DE VENTA ELECTRÓNICA",
  factura: "FACTURA ELECTRÓNICA",
  credit_memo: "NOTA DE CRÉDITO ELECTRÓNICA",
};

export const PERU_PRINTED_REPRESENTATION: Record<PeruDocumentKind, string> = {
  boleta: "Representación Impresa de la Boleta de Venta Electrónica",
  factura: "Representación Impresa de la Factura Electrónica",
  credit_memo: "Representación Impresa de la Nota de Crédito Electrónica",
};

export type PeruTableLayout = "wide" | "compact";

export interface PeruTicketConfig {
  previewTitle: string;
  downloadPrefix: string;
  printTitle: string;
  tableLayout: PeruTableLayout;
  consultationUrl: string;
  showReferenceBlock: boolean;
  showPolicy: boolean;
  showDiscountInTotals: boolean;
}

export const PERU_TICKET_CONFIG: Record<PeruDocumentKind, PeruTicketConfig> = {
  factura: {
    previewTitle: "Previsualización de Factura Electrónica",
    downloadPrefix: "factura-electronica",
    printTitle: "Factura",
    tableLayout: "wide",
    consultationUrl: "https://portal.efacturacion.pe/appefacturacion",
    showReferenceBlock: false,
    showPolicy: true,
    showDiscountInTotals: true,
  },
  boleta: {
    previewTitle: "Previsualización de Boleta de Venta Electrónica",
    downloadPrefix: "boleta-electronica",
    printTitle: "Boleta",
    tableLayout: "compact",
    consultationUrl: "https://www.efacturacion.pe",
    showReferenceBlock: false,
    showPolicy: true,
    showDiscountInTotals: true,
  },
  credit_memo: {
    previewTitle: "Previsualización de Nota de Crédito Electrónica",
    downloadPrefix: "nota-credito-electronica",
    printTitle: "Nota de crédito",
    tableLayout: "compact",
    consultationUrl: "https://www.efacturacion.pe",
    showReferenceBlock: true,
    showPolicy: false,
    showDiscountInTotals: false,
  },
};

const normalizeTipoDoc = (value?: string) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const getTaxId = (invoice?: any, selectedClient?: any) =>
  String(
    invoice?.VAT_Registration_No_ ||
      invoice?.VAT_Registration_No ||
      selectedClient?.Numero_Identificacion_fiscal ||
      "",
  ).replace(/\D/g, "");

const isRucCustomer = (invoice: any, selectedClient?: any) => {
  const tipoDoc = normalizeTipoDoc(
    invoice?.Tipo_Documento_SrI || selectedClient?.Tipo_Documento_SrI,
  );

  if (["RUC", "ID"].includes(tipoDoc)) return true;

  const digits = getTaxId(invoice, selectedClient);
  // Perú (11) y Ecuador (13) usan longitudes distintas para RUC.
  if (digits.length === 11 || digits.length === 13) return true;

  return false;
};

export const getPeruDocumentKind = (
  invoice: any,
  selectedClient?: any,
): PeruDocumentKind => {
  if (invoice?.Document_Type === "Credit Memo") return "credit_memo";

  const docNo = String(invoice?.No || "").toUpperCase();
  if (docNo.startsWith("BN")) return "credit_memo";
  if (docNo.startsWith("BP") || docNo.startsWith("BPR")) return "boleta";
  if (docNo.startsWith("FP") || docNo.startsWith("FPR")) return "factura";

  return isRucCustomer(invoice, selectedClient) ? "factura" : "boleta";
};

export const getCustomerIdLabel = (
  invoice: any,
  selectedClient?: any,
): string => {
  const tipoDoc = normalizeTipoDoc(
    invoice?.Tipo_Documento_SrI || selectedClient?.Tipo_Documento_SrI,
  );

  if (["RUC", "ID"].includes(tipoDoc)) return "RUC";
  if (tipoDoc === "CE") return "CE";
  if (tipoDoc === "PASAPORTE") return "CE";
  if (tipoDoc === "CEDULA") return "DNI";
  if (isRucCustomer(invoice, selectedClient)) return "RUC";
  return "DNI";
};

export const formatPeruDate = (value?: string) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return parsed.format("DD-MM-YYYY");
};

export const formatPeruTime = (invoice?: any) => {
  const raw =
    invoice?.Hora_Aprobacion ||
    invoice?.Hora_creacion ||
    invoice?.Posting_Time ||
    "";

  if (raw && /^\d{1,2}:\d{2}(:\d{2})?$/.test(String(raw).trim())) {
    const parts = String(raw).trim().split(":");
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${(parts[2] || "00").padStart(2, "0")}`;
  }

  const dateValue = invoice?.Posting_Date || invoice?.Document_Date;
  if (!dateValue) return dayjs().format("HH:mm:ss");

  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format("HH:mm:ss") : dayjs().format("HH:mm:ss");
};

export const amountToWordsSoles = (amount: number): string => {
  const safeAmount = Math.max(0, Number(amount) || 0);
  const intPart = Math.floor(safeAmount);
  const cents = Math.round((safeAmount - intPart) * 100);
  const letters = numberToLetters(intPart).toUpperCase();
  return `SON: ${letters} Y ${cents.toString().padStart(2, "0")}/100 SOLES`;
};

export interface PeruLineTotals {
  itemNo: number;
  code: string;
  sunatCode: string;
  quantity: number;
  unitOfMeasure: string;
  pUnit: number;
  vUnit: number;
  discountPct: number;
  saleValue: number;
  description: string;
}

export interface PeruTicketTotals {
  taxableBase: number;
  exemptBase: number;
  igv: number;
  total: number;
  totalDiscount: number;
  itemCount: number;
  igvPercent: number;
}

export const calculatePeruTicketData = (
  lines: any[] = [],
): { lineTotals: PeruLineTotals[]; totals: PeruTicketTotals } => {
  const lineTotals: PeruLineTotals[] = [];
  let taxableBase = 0;
  let exemptBase = 0;
  let igv = 0;
  let totalDiscount = 0;
  let itemCount = 0;
  let igvPercent = 0;

  lines.forEach((line, index) => {
    const quantity = Number(line.Quantity) || 0;
    const unitPrice = Number(line.Unit_Price) || 0;
    const discountPct = Number(line.Line_Discount_) || 0;
    const ivaPct = Number(line.PorIva) || 0;

    const amounts = calculateLineAmounts({
      unitPrice,
      qty: quantity,
      discountPct,
      ivaPct,
      priceIncludesVAT: line.Price_Includes_VAT,
    });

    const netUnit =
      quantity > 0 ? amounts.base / quantity : unitPrice * (1 - discountPct / 100);
    const pUnit =
      ivaPct > 0 ? netUnit * (1 + ivaPct / 100) : netUnit;
    const lineDiscount = quantity * unitPrice * (discountPct / 100);

    if (ivaPct > 0) {
      taxableBase += amounts.base;
      igv += amounts.vat;
      if (!igvPercent) igvPercent = ivaPct;
    } else {
      exemptBase += amounts.base;
    }

    totalDiscount += lineDiscount;
    itemCount += quantity;

    lineTotals.push({
      itemNo: index + 1,
      code: line.No || "",
      sunatCode:
        line.Codigo_Sunat ||
        line.Codigo_SUNAT ||
        line.GTIN ||
        line.Barcode ||
        "", // TODO: Dato backend - confirmar campo Código Sunat en líneas
      quantity,
      unitOfMeasure: line.Unit_of_Measure_Code || "NIU",
      pUnit,
      vUnit: unitPrice,
      discountPct,
      saleValue: amounts.base,
      description: line.Description || "",
    });
  });

  return {
    lineTotals,
    totals: {
      taxableBase,
      exemptBase,
      igv,
      total: taxableBase + exemptBase + igv,
      totalDiscount,
      itemCount,
      igvPercent: igvPercent || 18,
    },
  };
};

export const getQrImageSrc = (invoice?: any): string | null => {
  const raw = invoice?.GenerateQR || invoice?.QR_Code || "";
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:image")) return trimmed;
  if (trimmed.startsWith("http")) return trimmed;
  return `data:image/png;base64,${trimmed}`;
};

export const getReferenceDocumentLabel = (invoice?: any): string => {
  const refNo = String(invoice?.Applies_to_Doc_No || "").toUpperCase();
  if (refNo.startsWith("BP") || refNo.startsWith("BPR")) return "BOLETA DE VENTA";
  if (refNo.startsWith("FP") || refNo.startsWith("FPR")) return "FACTURA";
  return invoice?.Applies_to_Doc_Type === "Invoice"
    ? "FACTURA"
    : "BOLETA DE VENTA";
};
